// @ts-check
import React from 'react';
import { Grid, Typography } from '@material-ui/core';
import PropTypes from 'prop-types';
import {
  GiftCardPaymentOptionsPropTypes,
  isValueZeroFromCurrencyString,
  PaymentOptionsPropTypes,
  ShipmentMethodPropTypes,
} from 'utils';
import { TRANSACTION_TYPES } from 'constants/index';
import ReceiptPaymentField from 'sharedComponents/ReceiptPaymentField';
import ReceiptGiftCardField from 'sharedComponents/ReceiptGiftCardField';

const receiptFields = [
  { key: 'subTotalPrice', label: 'Subtotal' },
  { key: 'promotion', label: 'Promotion' },
  { key: 'giftPackaging', label: 'Gift Packaging' },
  { key: 'deliveryCharges', label: 'Shipping' },
  { key: 'orderTotalTax', label: 'Tax' },
  { key: 'giftCard', label: 'Gift Card' },
];

const ReceiptFields = ({
  classes,
  method,
  isNewStyle,
  storeSaleType,
  isBgElevatedPackagingEligible,
  isRefundOrder,
  isSendsTakesOrder,
  isInstore,
  hideGiftCards,
  payments,
  gcPayments,
}) => {
  const skipGiftCards = hideGiftCards || isInstore || isValueZeroFromCurrencyString(method.giftCard);
  const skipPromotion = isValueZeroFromCurrencyString(method.promotion);
  const skipGiftPackaging = isValueZeroFromCurrencyString(method.giftPackaging);
  const hideDeliveryField = isSendsTakesOrder || isRefundOrder || storeSaleType === TRANSACTION_TYPES.TAKES;

  const isElevatedPackaging = (key) => {
    if (key === 'giftPackaging') {
      return isBgElevatedPackagingEligible;
    }
  };

  const renderReceiptField = ({ key, label, promoArray, i = 0 }) => {
    let receiptValue = method[key];
    if (promoArray?.length) {
      receiptValue = promoArray.join(', ');
    } else if (isElevatedPackaging(key)) {
      receiptValue = '$0.00';
    }

    const isPromoOrShipping = key === 'promotion' || key === 'deliveryCharges';
    const textColorDisplay = method[key]?.includes('-') && !isPromoOrShipping ? 'error' : 'textPrimary';

    return (
      <Grid container justifyContent="space-between" key={key}>
        <Typography
          className={isNewStyle ? classes.paymentLabel : null}
          variant="body2"
          color="textSecondary"
          gutterBottom
          data-testid={`order-receipt-${key}`}
        >
          {i === 0 ? label : ''}
        </Typography>
        <Typography
          variant="body2"
          color={textColorDisplay}
          gutterBottom
          data-testid={`order-receipt-${key}`}
          className={isNewStyle ? classes.paymentValue : null}
          key={isBgElevatedPackagingEligible}
        >
          {isRefundOrder && receiptValue !== '$0.00' && '- '}
          {receiptValue}
        </Typography>
      </Grid>
    );
  };

  return (
    <>
      {receiptFields.map(({ key, label }) => {
        if (key === 'deliveryCharges' && hideDeliveryField) return;
        if (key === 'giftCard' && skipGiftCards) return;
        if (key === 'promotion' && skipPromotion) return;
        if (key === 'giftPackaging' && skipGiftPackaging) return;

        if (key === 'promotion') {
          const multiplePromos = method[key] && Array.isArray(method[key]);

          return multiplePromos
            ? method[key].map((promoArray, i) => renderReceiptField({ key, label, promoArray, i }))
            : method[key] && renderReceiptField({ key, label, promoArray: null });
        }
        if (key === 'deliveryCharges') {
          return renderReceiptField({ key, label, promoArray: null });
        }
        return method[key] && renderReceiptField({ key, label, promoArray: null });
      })}

      {!!isInstore &&
        payments.map((payment) => (
          <ReceiptPaymentField key={payment.keyIdentifier} payment={payment} isNewStyle={isNewStyle} />
        ))}

      {!!isInstore &&
        gcPayments.map((giftCard) => (
          <ReceiptGiftCardField key={giftCard.giftCardNumber} giftCard={giftCard} isNewStyle={isNewStyle} />
        ))}
    </>
  );
};

ReceiptFields.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  method: ShipmentMethodPropTypes,
  isNewStyle: PropTypes.bool,
  storeSaleType: PropTypes.string,
  isBgElevatedPackagingEligible: PropTypes.bool,
  isRefundOrder: PropTypes.bool,
  isSendsTakesOrder: PropTypes.bool,
  hideGiftCards: PropTypes.bool,
  isInstore: PropTypes.bool,
  payments: PaymentOptionsPropTypes,
  gcPayments: GiftCardPaymentOptionsPropTypes,
};

ReceiptFields.defaultProps = {
  classes: {},
  method: {},
  isNewStyle: false,
  storeSaleType: '',
  isBgElevatedPackagingEligible: false,
  isRefundOrder: false,
  isSendsTakesOrder: false,
  hideGiftCards: false,
  isInstore: false,
  payments: [],
  gcPayments: [],
};

export default ReceiptFields;
